/**
 * Licensed to the Apache Software Foundation (ASF) under one
 * or more contributor license agreements.  See the NOTICE file
 * distributed with this work for additional information
 * regarding copyright ownership.  The ASF licenses this file
 * to you under the Apache License, Version 2.0 (the
 * "License"); you may not use this file except in compliance
 * with the License.  You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing,
 * software distributed under the License is distributed on an
 * "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY
 * KIND, either express or implied.  See the License for the
 * specific language governing permissions and limitations
 * under the License.
 */
import { t, styled } from '@superset-ui/core';
import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import Alert from 'src/components/Alert';
import IndeterminateCheckbox from 'src/components/IndeterminateCheckbox';
import TableCollection from 'src/components/TableCollection';

import trash_bin from 'packages/superset-ui-chart-controls/src/components/ColumnTypeLabel/type-icons/trash_bin.svg';
import export_icon from 'packages/superset-ui-chart-controls/src/components/ColumnTypeLabel/type-icons/export.svg';

import { HomeTabcontext } from 'src/views/CRUD/SearchContext';
// import Button from '../Button';
import { EmptyStateBig, EmptyStateProps } from 'src/components/EmptyState';
import Icons from 'src/components/Icons';

import SelectFilter from './Filters/Select';
import { CardLabel } from '../../types';

import CardCollection from './CardCollection';
import FilterControls from './Filters';
import { CardSortSelect } from './CardSortSelect';

import {
  FetchDataConfig,
  Filters,
  SortColumn,
  CardSortSelectOption,
  ViewModeType,
  SelectOption,
} from './types';
import { ListViewError, useListViewState } from './utils';


const ListViewStyles = styled.div`
  padding:0;
  text-align: center;
  background:white;
  .superset-list-view {
    text-align: left;
    border-radius: 4px 0;
    margin: 0;

    .header {

      // height: 60px;
      display: flex;
      flex-direction: column;
      align-items: start;
      justify-content: center;
      gap: 2rem;
      padding: 1rem 1.6rem;

      // .header-left {
      //   padding-left:16px;
      //   display: flex;
      // }
      // .header-right {
      //   padding-right:16px;
      // }
      & .controls {
        display: flex;
        flex-wrap: wrap;
        column-gap: ${({ theme }) => theme.gridUnit * 6}px;
        row-gap: ${({ theme }) => theme.gridUnit * 4}px;
      }
    }

    .body.empty table {
      margin-bottom: 0;
    }

    .body {
      overflow-x: auto;
      height:100%;
      .ant-alert, .ant-alert-info{
        background-color:white;
      }
    }

    .ant-empty {
      .ant-empty-image {
        height: auto;
      }
    }
  }

  .pagination-container {
    display: flex;
    flex-direction: row;
    justify-content: flex-end;
    align-items:center;
    margin-bottom: ${({ theme }) => theme.gridUnit * 4}px;
    margin-right: ${({ theme }) => theme.gridUnit * 4}px;
  }

  .row-count-container {
    color: ${({ theme }) => theme.colors.grayscale.base};
  }
`;

// .selectedCopy {
//   display: inline-block;
//   padding: ${({ theme }) => theme.gridUnit * 2}px 0;
//   color:${({ theme }) => theme.colors.primary.base};
// }
const BulkSelectWrapper = styled(Alert)`
  ${({ theme }) => `
    border-radius: 0;
    margin-bottom: 0;
    color: ${theme.colors.grayscale.dark1};
    background-color: ${theme.colors.primary.light4};
    
    .selectedCopy {
      display: inline-block;
      padding: ${theme.gridUnit * 2}px 0;
    }

    .deselect-all {
      color: ${theme.colors.primary.base};
      margin-left: ${theme.gridUnit * 4}px;
    }

    .divider {
      margin: ${`${-theme.gridUnit * 2}px 0 ${-theme.gridUnit * 2}px ${
        theme.gridUnit * 4
      }px`};
      width: 1px;
      height: ${theme.gridUnit * 8}px;
      box-shadow: inset -1px 0px 0px ${theme.colors.grayscale.light2};
      display: inline-flex;
      vertical-align: middle;
      position: relative;
    }

    .ant-alert-close-icon {
      margin-top: ${theme.gridUnit * 1.5}px;
    }
  `}
`;

const bulkSelectColumnConfig = {
  Cell: ({ row }: any) => (
    <IndeterminateCheckbox {...row.getToggleRowSelectedProps()} id={row.id} />
  ),
  Header: ({ getToggleAllRowsSelectedProps }: any) => (
    <IndeterminateCheckbox
      {...getToggleAllRowsSelectedProps()}
      id="header-toggle-all"
    />
  ),
  id: 'selection',
  size: 'sm',
};

// const ViewModeContainer = styled.div`
//   padding-right: ${({ theme }) => theme.gridUnit * 4}px;
//   margin-top: ${({ theme }) => theme.gridUnit * 5 + 1}px;
//   white-space: nowrap;
//   display: inline-block;

//   .toggle-button {
//     display: inline-block;
//     border-radius: ${({ theme }) => theme.gridUnit / 2}px;
//     padding: ${({ theme }) => theme.gridUnit}px;
//     padding-bottom: ${({ theme }) => theme.gridUnit * 0.5}px;

//     &:first-of-type {
//       margin-right: ${({ theme }) => theme.gridUnit * 2}px;
//     }
//   }

//   .active {
//     // background-color: ${({ theme }) => theme.colors.grayscale.base};
//     svg path{
//       fill: ${({ theme }) => theme.colors.primary.base};
//     }
//   }
// `;

const EmptyWrapper = styled.div`
  padding: ${({ theme }) => theme.gridUnit * 40}px 0;

  &.table {
    background: ${({ theme }) => theme.colors.grayscale.light5};
  }
`;

// const ViewModeToggle = ({
//   mode,
//   setMode,
// }: {
//   mode: 'table' | 'card';
//   setMode: (mode: 'table' | 'card') => void;
// }) => (
//   <ViewModeContainer>
//     <div
//       role="button"
//       tabIndex={0}
//       onClick={e => {
//         e.currentTarget.blur();
//         setMode('card');
//       }}
//       className={cx('toggle-button', { active: mode === 'card' })}
//     >
//       <Icons.CardView />
//     </div>
//     <div
//       role="button"
//       tabIndex={0}
//       onClick={e => {
//         e.currentTarget.blur();
//         setMode('table');
//       }}
//       className={cx('toggle-button', { active: mode === 'table' })}
//     >
//       <Icons.ListView />
//     </div>
//   </ViewModeContainer>
// );

const ListHeader = styled.div`
  font-weight: 700;
  font-size: 1rem;
  line-height: 1.1rem;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  width: 100%;
`;

export interface ListViewProps<T extends object = any> {
  columns: any[];
  data: T[];
  count: number;
  pageSize: number;
  fetchData: (conf: FetchDataConfig) => any;
  loading: boolean;
  className?: string;
  initialSort?: SortColumn[];
  filters?: Filters;
  bulkActions?: Array<{
    key: string;
    name: React.ReactNode;
    onSelect: (rows: any[]) => any;
    type?: 'primary' | 'secondary' | 'danger';
  }>;
  bulkSelectEnabled?: boolean;
  disableBulkSelect?: () => void;
  renderBulkSelectCopy?: (selects: any[]) => React.ReactNode;
  renderCard?: (row: T & { loading: boolean }) => React.ReactNode;
  cardSortSelectOptions?: Array<CardSortSelectOption>;
  defaultViewMode?: ViewModeType;
  highlightRowId?: number;
  showThumbnails?: boolean;
  emptyState?: EmptyStateProps;
  activeViewType: CardLabel;
  handleViewTypeChange: (tab: CardLabel) => void;
}

function ListView<T extends object = any>({
  columns,
  data,
  count,
  pageSize: initialPageSize,
  fetchData,
  loading,
  initialSort = [],
  className = '',
  filters = [],
  bulkActions = [],
  bulkSelectEnabled = false,
  disableBulkSelect = () => {},
  renderBulkSelectCopy = selected => t('%s Selected', selected.length),
  renderCard,
  showThumbnails,
  cardSortSelectOptions,
  defaultViewMode = 'card',
  highlightRowId,
  emptyState,
  activeViewType,
  handleViewTypeChange
}: ListViewProps<T>) {
  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    prepareRow,
    pageCount = 1,
    gotoPage,
    applyFilterValue,
    selectedFlatRows,
    toggleAllRowsSelected,
    // setViewMode,
    state: { pageIndex, pageSize, internalFilters, viewMode },
    query,
  } = useListViewState({
    bulkSelectColumnConfig,
    bulkSelectMode: bulkSelectEnabled && Boolean(bulkActions.length),
    columns,
    count,
    data,
    fetchData,
    initialPageSize,
    initialSort,
    initialFilters: filters,
    renderCard: Boolean(renderCard),
    defaultViewMode,
  });
  const filterable = Boolean(filters.length);
  if (filterable) {
    const columnAccessors = columns.reduce(
      (acc, col) => ({ ...acc, [col.id || col.accessor]: true }),
      {},
    );
    filters.forEach(f => {
      if (!columnAccessors[f.id]) {
        throw new ListViewError(
          `Invalid filter config, ${f.id} is not present in columns`,
        );
      }
    });
  }

  const {currentTab} = useContext(HomeTabcontext);
  
  const filterControlsRef = useRef<{ clearFilters: () => void }>(null);

  const handleClearFilterControls = useCallback(() => {
    if (query.filters) {
      filterControlsRef.current?.clearFilters();
    }
  }, [query.filters]);

  // const cardViewEnabled = Boolean(renderCard);

  useEffect(() => {
    // discard selections if bulk select is disabled
    if (!bulkSelectEnabled) toggleAllRowsSelected(false);
  }, [bulkSelectEnabled, toggleAllRowsSelected]);


  const [,setEmptyIcon] = useState('');
  const [empty_btn,setEmptyBtn] = useState('');
  const [,setEmptyDesc] = useState('');
  const getEmptyTableProp = ()=>{
    // console.log(currentTab);
    // console.log(window.location.pathname);
    if(window.location.pathname != '/superset/welcome/'){
      setEmptyIcon('');
      setEmptyDesc("");
      setEmptyBtn('');
      return;
    }
    switch (currentTab) {
      case "chart":
          setEmptyIcon('chart');
          setEmptyDesc("You don't have any chart to show, Please create a Chart.");
          setEmptyBtn('Create New Chart');
        break;
      case "dashboard":
        setEmptyIcon('dashboard');
        setEmptyDesc("You don't have any Dashboard to show, Please create a Dashboard.");
        setEmptyBtn('Create New Dashboard');
        break;
      case "dataset":
        setEmptyIcon('dataset');
        setEmptyDesc("You don't have any Dataset to show, Please add a Dataset.");
        setEmptyBtn('Add Dataset');
        break;
      case "database":
        setEmptyIcon('database');
        setEmptyDesc("You don't have any Database to show, Please add a Database.");
        setEmptyBtn('Add Database');
        break;
      default:
        setEmptyIcon('');
        setEmptyDesc("");
        setEmptyBtn('');
        break;
    }

    // console.log(empty_btn);
  }
  useEffect(()=>{
    getEmptyTableProp();
  },[])

  const EmptyContainer = styled.div`
    text-align:center;
    .description{
      font-style: normal;
      font-weight: 500;
      font-size: 13px;
      line-height: 16px;
      color: #333333;
    }
  `;

  const options: SelectOption[] = [
    { label: t('Dashboard'), value: CardLabel.Dashboard },
    { label: t('Chart'), value: CardLabel.Chart },
    { label: t('Dataset'), value: CardLabel.Dataset },
    { label: t('Database'), value: CardLabel.Database },
  ];

  return (
    <ListViewStyles>
      <div data-test={className} className={`superset-list-view ${className}`}>
        <div className="header">
          {/* {cardViewEnabled && (
            <ViewModeToggle mode={viewMode} setMode={setViewMode} />
          )} */}
          <ListHeader>
            <span>
              All List
            </span>
            {filterable && (
              <FilterControls
                ref={filterControlsRef}
                filters={[filters[filters.length - 1]]}
                internalFilters={internalFilters}
                searchIndex={filters.length - 1}
                updateFilterValue={applyFilterValue}
              />
            )}
          </ListHeader>
          <div className="controls">
            <SelectFilter
              Header={'Type'}
              initialValue={{label: t(activeViewType), value: activeViewType}}
              name={'viz_type_select'}
              onSelect={(option: SelectOption | undefined) => {
                  if (option) {
                    handleViewTypeChange(option.value as CardLabel);
                  }
                }
              }
              paginate={true}
              selects={options}
            />
            {filterable && (
              <FilterControls
                ref={filterControlsRef}
                filters={filters.slice(0, filters.length - 1)}
                internalFilters={internalFilters}
                updateFilterValue={applyFilterValue}
              />
            )}
            {viewMode === 'card' && cardSortSelectOptions && (
              <CardSortSelect
                initialSort={initialSort}
                onChange={fetchData}
                options={cardSortSelectOptions}
                pageIndex={pageIndex}
                pageSize={pageSize}
              />
            )}
          </div>
        </div>
        <div className={`body ${rows.length === 0 ? 'empty' : ''}`}>
          {bulkSelectEnabled && Boolean(selectedFlatRows.length) && (
            <BulkSelectWrapper
              data-test="bulk-select-controls"
              type="info"
              closable
              showIcon={false}
              onClose={()=>toggleAllRowsSelected(false)}
              message={
                <>
                  <div className="selectedCopy" data-test="bulk-select-copy">
                    {renderBulkSelectCopy(selectedFlatRows)}
                  </div>
                  {Boolean(selectedFlatRows.length) && (
                    <>
                      {/* <span
                        data-test="bulk-select-deselect-all"
                        role="button"
                        tabIndex={0}
                        className="deselect-all"
                        onClick={() => toggleAllRowsSelected(false)}
                      >
                        {t('Deselect all')}
                      </span> */}
                      <div className="divider" />
                      {bulkActions.map(action => (
                        // <Button
                        //   data-test="bulk-select-action"
                        //   key={action.key}
                        //   buttonStyle={'link'}
                        //   cta
                        //   onClick={() =>
                        //     action.onSelect(
                        //       selectedFlatRows.map(r => r.original),
                        //     )
                        //   }
                        //   className={action.type}
                        // >
                        //   {action.name}
                        // </Button>

                        <img
                          data-test="bulk-select-action"
                          key={action.key}
                          onClick={() =>
                            action.onSelect(
                              selectedFlatRows.map(r => r.original),
                            )
                          }
                          className={'select-'+action.type}
                          style={{marginLeft:'10px',marginRight:'10px',cursor:'pointer'}}
                          src={action.type=='danger'?trash_bin:export_icon}
                        />
                      ))}
                    </>
                  )}
                </>
              }
            />
          )}
          {viewMode === 'card' && (
            <CardCollection
              bulkSelectEnabled={bulkSelectEnabled}
              prepareRow={prepareRow}
              renderCard={renderCard}
              rows={rows}
              loading={loading}
              showThumbnails={showThumbnails}
            />
          )}
          {viewMode === 'table' && (
            <TableCollection
              getTableProps={getTableProps}
              getTableBodyProps={getTableBodyProps}
              prepareRow={prepareRow}
              headerGroups={headerGroups}
              rows={rows}
              columns={columns}
              loading={loading}
              highlightRowId={highlightRowId}
            />
          )}
          {!loading && rows.length === 0 &&  empty_btn && (
            <EmptyWrapper className={viewMode}>
              {/* <EmptyContainer>
                <div className="icon"><img src={EmptyTableIcons[empty_icon]} alt="" /></div>
                <div className="description" style={{display:'flex',alignItems:'center',justifyContent:'center',marginBottom:'12px',marginTop:'12px'}}>
                  <div style={{textAlign:'center',width:'232px'}}>{empty_desc}</div>
                </div>
                <div>
                  <Button buttonStyle="primary" onClick={()=>{btnFun()}}>
                    {empty_btn}
                  </Button>
                </div>
            </EmptyContainer> */}

              
              {query.filters ? (
                <EmptyStateBig
                  title={t('No results match your filter criteria')}
                  description={t('Try different criteria to display results.')}
                  image="filter-results.svg"
                  buttonAction={() => handleClearFilterControls()}
                  buttonText={t('clear all filters')}
                />
              ) : (
                <EmptyStateBig
                  {...emptyState}
                  title={emptyState?.title || t('No Data')}
                  image={emptyState?.image || 'filter-results.svg'}
                />
              )}
            </EmptyWrapper>
            
          )}
          { !loading && rows.length ===0 && !empty_btn && 
            (
              <EmptyWrapper className={viewMode}>
                <EmptyContainer>
                  <div className="description" style={{display:'flex',alignItems:'center',justifyContent:'center',marginBottom:'12px',marginTop:'12px'}}>
                    <div style={{textAlign:'center',width:'232px'}}>No Data Found</div>
                  </div>
              </EmptyContainer>

                
              </EmptyWrapper>
            )

          }
        </div>
      </div>

      {rows.length > 0 && (
        <div className="pagination-container">
          <div className="row-count-container">
            {!loading &&
              t(
                '%s of %s',
               pageIndex+1,
                pageCount,
              )}
          </div>
          {/* <Pagination
            totalPages={pageCount || 0}
            currentPage={pageCount ? pageIndex + 1 : 0}
            onChange={(p: number) => gotoPage(p - 1)}
            hideFirstAndLastPageLinks
          /> */}
          
          <span style={{marginLeft:'12px',marginRight:'12px', cursor:'pointer'}} onClick={()=>gotoPage(pageIndex-1)}>
            {/* <img src={prev_page_icon} style={{cursor:'pointer'}}/> */}
            <Icons.PrevPage />
          </span>
          <span style={{marginRight:'8px', cursor:'pointer'}} onClick={()=>gotoPage(pageIndex+1)}>
            {/* <img src={next_page_icon} style={{cursor:'pointer'}}/> */}
            <Icons.NextPage />
          </span>
        </div>
      )}
    </ListViewStyles>
  );
}

export default ListView;
